// KycSectionRiskAssessment.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { auth, db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  ISO3_TO_ISO2,
  countryNameKeysForIso3,
  normalizeCountryLookupName,
  toIso3,
} from "../../../utils/iso2to3";
import {
  DEFAULT_COUNTRY_RISK_RULES,
  combineGeoLevels,
  evaluateCountryRiskForIso3,
  normalizeUboCountryRiskScope,
  sanitizeCountryRiskRules,
} from "../../../utils/countryRiskRules";
import {
  getSelectedIndividualUboRows,
  ownershipCountrySignature,
} from "../../../utils/uboSelection";
import { AnimatePresence, motion } from "framer-motion";
import { CircleFlag } from "react-circle-flags";
import Lottie from "lottie-react";
import geoAnim from "../../../images/geo-lottie.json";
import industryAnim from "../../../images/factory-lottie.json";
import doubleTapFullIcon from "../../../images/double-tap-full.svg";
import { MYKYC_API_BASE } from "../../../config/api";

// ✅ add this file (CSS snippet provided below)
import "../css/KycSectionRiskAssessment.css";
import "../css/KycSectionPepSanctionWithScreening.css";

// ✅ Backend
const API_BASE = MYKYC_API_BASE;
const INDUSTRY_PATH = "/myKyc/analysis/activity-category";
const NO_CATEGORY = "no category identified";

async function apiFetch(path, { method = "POST", token, body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "Request failed";
    const err = new Error(msg);
    err.status = res.status;
    err.code = json?.code;
    throw err;
  }

  return json;
}

function userFriendlyIndustryError(err) {
  const msg = String(err?.message || "").trim();
  if (msg.includes("Industry category lists are empty in Firestore")) {
    console.warn("[industry risk] missing category lists:", msg);
    return "No industry categories are recorded for this organisation.";
  }
  return msg || "Failed to load industry risk.";
}

function clean(value) {
  return String(value ?? "").trim();
}

function buildScreeningUsageContext(form, screeningType) {
  return {
    orgId: clean(form?.orgId || form?.customer?.orgId || ""),
    clientId: clean(form?.clientId || form?.id || form?.customer?.clientId || ""),
    screeningType,
    source: "kyc",
  };
}

function badge(text, scheme = "secondary") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}

function customListBadge(text, tier = "") {
  const cleanTier = String(tier || "").toLowerCase();
  const tierClass =
    cleanTier === "high"
      ? "badgeKycRed"
      : cleanTier === "medium"
      ? "badgeKycYellow"
      : cleanTier === "standard" || cleanTier === "low"
      ? "badgeKycGreen"
      : "badgeKycGrey";
  return <span className={`badge badgeStandard ${tierClass}`}>{text}</span>;
}

function autoResizeTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight + verticalExtras;
  const maxHeight = lineHeight * 15 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

function shortBusinessActivityFallback(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const words = text.split(/\s+/).filter(Boolean);
  return words.length <= 25 ? text : "";
}

function ManualIndustryRiskDropdown({
  value,
  options,
  onChange,
  autoOpen = false,
  showPlaceholderWhenOpen = false,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((o) => Number(o.value) === Number(value));
  const controlSelected = open && showPlaceholderWhenOpen ? null : selected;

  const updateMenuPosition = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = 160;
    const margin = 12;
    const left = Math.min(
      Math.max(margin, rect.left),
      Math.max(margin, window.innerWidth - width - margin)
    );

    setMenuPos({
      top: rect.bottom + 6,
      left,
      width,
    });
  };

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onMouseDown(e) {
      const isInsideControl = wrapRef.current?.contains(e.target);
      const isInsideMenu = menuRef.current?.contains(e.target);
      if (!isInsideControl && !isInsideMenu) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const menu = open ? (
    <div
      className="industryManualRiskMenu industryManualRiskMenuPortal"
      role="listbox"
      ref={menuRef}
      style={
        menuPos
          ? {
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              width: `${menuPos.width}px`,
            }
          : undefined
      }
    >
      {options.map((o) => {
        const isSelected = Number(o.value) === Number(value);
        return (
          <button
            key={o.value}
            type="button"
            className={`industryManualRiskOption is-${o.tone} ${
              isSelected ? "is-selected" : ""
            }`}
            role="option"
            aria-selected={isSelected}
            onClick={() => {
              onChange(o.value);
              setOpen(false);
            }}
          >
            <span
              className={`industryManualRiskOptionDot is-${o.tone}`}
              aria-hidden="true"
            />
            <span className="industryManualRiskOptionText">
              <span>{o.label}</span>
            </span>
            {isSelected ? <i className="bi bi-check2" aria-hidden="true" /> : null}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="industryManualRiskDropdown" ref={wrapRef}>
      <button
        type="button"
        className={`industryManualRiskControl ${
          controlSelected ? `is-${controlSelected.tone}` : ""
        }`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Choose the industry risk level"
      >
        <span
          className={`industryManualRiskDot ${
            controlSelected ? `is-${controlSelected.tone}` : ""
          }`}
          aria-hidden="true"
        />
        <span className="industryManualRiskLabel">
          {controlSelected?.label || "Choose risk"}
        </span>
        <i className="bi bi-chevron-down" aria-hidden="true" />
      </button>

      {menu && typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : null}
    </div>
  );
}

function cpiPill(score) {
  if (score == null) return badge("CPI: n/a", "secondary");
  const s = Number(score);
  if (!Number.isFinite(s)) return badge("CPI: n/a", "secondary");
  if (s >= 70) return badge(`CPI: ${s}`, "success");
  if (s >= 40) return badge(`CPI: ${s}`, "warning");
  return badge(`CPI: ${s}`, "danger");
}

function baselPill(score) {
  if (score == null) return badge("Basel AML: n/a", "secondary");
  const s = Number(score);
  if (!Number.isFinite(s)) return badge("Basel AML: n/a", "secondary");
  const label = s.toFixed(1);
  if (s > 7) return badge(`Basel AML: ${label}`, "danger");
  if (s >= 5.5) return badge(`Basel AML: ${label}`, "warning");
  return badge(`Basel AML: ${label}`, "success");
}

function readCountryScore(scores, iso3) {
  const key = String(iso3 || "").trim().toUpperCase();
  if (!key || !scores || typeof scores !== "object") return null;

  const direct = scores[key];
  if (direct != null) return direct;

  const iso2 = ISO3_TO_ISO2[key];
  if (iso2 && scores[iso2] != null) return scores[iso2];

  const matchingKey = Object.keys(scores).find((candidate) => {
    const normalized = String(candidate || "").trim().toUpperCase();
    return normalized === key || (iso2 && normalized === iso2);
  });
  if (matchingKey) return scores[matchingKey];

  const countryNameKeys = countryNameKeysForIso3(key);
  const countryNameKey = Object.keys(scores).find((candidate) =>
    countryNameKeys.includes(normalizeCountryLookupName(candidate))
  );

  return countryNameKey ? scores[countryNameKey] : null;
}

function riskLevelPill(scoreLevel) {
  if (scoreLevel == null) return badge("Risk: n/a", "secondary");

  const lvl = Number(scoreLevel);
  if (!Number.isFinite(lvl)) return badge("Risk: n/a", "secondary");

  if (lvl <= 1) return badge(`Standard Risk`, "success");
  if (lvl === 2) return badge(`Medium Risk`, "warning");
  return badge(`High Risk`, "danger");
}

function riskLevelText(scoreLevel) {
  const lvl = Number(scoreLevel);
  if (!Number.isFinite(lvl)) return "n/a";
  if (lvl <= 1) return "Standard risk";
  if (lvl === 2) return "Medium risk";
  return "High risk";
}

// --------- subtle motion presets (discreet) ----------
const EASE = [0.22, 1, 0.36, 1];
const LAYOUT_TRANSITION = { duration: 0.28, ease: EASE };

const expandCollapse = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.28, ease: EASE },
};

// ----- HELPERS RISK CALCULATION & EXPORT RISKS TO FINAL OVERVIEW ------

function geoLevelFromCountryRisk(r) {
  if (!r?.iso3) return null;

  const customTier = String(r.customTier || "").toLowerCase();
  if (customTier === "high") return "high";
  if (customTier === "medium") return "medium";
  if (customTier === "standard") return "low";

  const defaultTier = String(r.defaultTier || "").toLowerCase();
  if (defaultTier === "high") return "high";
  if (defaultTier === "medium") return "medium";
  if (defaultTier === "standard") return "low";

  return "low";
}

function maxGeoLevel(a, b) {
  const order = { low: 1, medium: 2, high: 3 };
  const aa = a && order[a] ? order[a] : 0;
  const bb = b && order[b] ? order[b] : 0;
  const top = Math.max(aa, bb);
  return top === 3 ? "high" : top === 2 ? "medium" : top === 1 ? "low" : null;
}

/* -------------------- Modern Gauge -------------------- */

function gaugeModel(level) {
  // colors: green/yellow/red like you asked
  if (level === "low") return { pct: 0.22, label: "Standard", tone: "low" };
  if (level === "medium") return { pct: 0.5, label: "Medium", tone: "medium" };
  if (level === "high") return { pct: 0.82, label: "High", tone: "high" };
  return { pct: 0, label: "—", tone: "na" };
}

function Gauge({ title, level, rightIcon = null }) {
  const { pct, label, tone } = gaugeModel(level);

  const needleDeg = -150 + pct * 120;

  const cx = 60,
    cy = 60,
    r = 44;
  const startX = cx - r,
    startY = cy;
  const endX = cx + r,
    endY = cy;
  const arc = `M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`;

  const arcLen = Math.PI * r;
  const dash = `${arcLen * pct} ${arcLen}`;

  return (
    <div className="kycRiskGauge">
      {title ? (
        <div className="kycRiskGaugeTitle" style={{ marginBottom: 6 }}>
          {title}
        </div>
      ) : null}

      {/* ✅ inside border: pill left, gauge mid, icon right */}
      <div className="kycRiskGaugeBodySide">
        {/* LEFT side: badge on top, gauge below */}
        <div className="kycRiskGaugeLeftStack">
          <div className={`kycRiskGaugePill kycRiskGaugePill-${tone}`}>
            {label}
          </div>

          <svg
            viewBox="0 0 120 70"
            className="kycRiskGaugeSvg"
            aria-hidden="true"
          >
            <path d={arc} className="kycRiskGaugeArcBg" />
            <path
              d={arc}
              className={`kycRiskGaugeArcFill kycRiskGaugeArcFill-${tone}`}
              style={{ strokeDasharray: dash }}
            />
            <g transform={`translate(${cx} ${cy}) rotate(${needleDeg})`}>
              <rect
                x="0"
                y="-2.3"
                width="42"
                height="4.6"
                rx="2.3"
                className="kycRiskGaugeNeedle"
              />
            </g>
            <circle cx={cx} cy={cy} r="5.2" className="kycRiskGaugeHub" />
          </svg>
        </div>

        {/* RIGHT side: big icon centered */}
        {rightIcon ? (
          <div className="kycRiskGaugeIconBig">{rightIcon}</div>
        ) : null}
      </div>
    </div>
  );
}

// --------------- EXPORT DEFAULT FUNCTION --------------

export default function KycSectionRiskAssessment({
  sectionNo = 0,
  form,
  orgId,
  cpiYear = 2024,
  baselYear = 2025,
  onIndustryResult,
  onGeoResult,
  // Overriding the final risk is a compliance decision — Staff front views only.
  readOnly = false,
}) {
  const customer = form?.customer || {};
  const ownership = form?.ownership || {};
  const isEntity = customer.type === "entity";

  const businessActivity = String(customer.businessActivity || "").trim();
  const prevBusinessActivityRef = useRef(businessActivity);
  const industryAutoRunTimerRef = useRef(null);
  const initialIndustryAutoloadDoneRef = useRef(false);

  // ✅ Hydrate Industry UI from saved results
  useEffect(() => {
    const saved = form?.internalUse?.industryRiskResult;
    if (!saved) return;

    if (saved?.auto) setIndustryAuto(saved.auto);
    if (saved?.final) {
      setIndustryFinal({
        mode: saved.final.mode || "pending",
        riskLevel: saved.final.riskLevel ?? null,
        reason: saved.final.reason || "",
        decidedAt: saved.final.decidedAt ?? null,
      });
    }
  }, [form?.internalUse?.industryRiskResult]);

  useEffect(() => {
    const saved = form?.internalUse?.industryRiskResult;
    if (saved === null) {
      setIndustryAuto(null);
      setIndustryFinal({
        mode: "pending",
        riskLevel: null,
        reason: "",
        decidedAt: null,
      });
      setShowOverride(false);
      setIndustryErr("");
      setLoadingIndustry(false);
    }
  }, [form?.internalUse?.industryRiskResult]);

  const isoIncorpOrNat = useMemo(
    () => toIso3(customer.nationalityOrIncorporationCountry),
    [customer.nationalityOrIncorporationCountry]
  );

  const isoDomicile = useMemo(
    () => (isEntity ? "" : toIso3(customer.country)),
    [customer.country, isEntity]
  );

  // ---------------- GEO DATA ----------------
  const [loadingGeo, setLoadingGeo] = useState(true);
  const [geoErr, setGeoErr] = useState("");
  const [showGeoDetails, setShowGeoDetails] = useState(false);
  const [showIndustryDetails, setShowIndustryDetails] = useState(false);

  const [lists, setLists] = useState({
    fatfGrey: [],
    fatfBlack: [],
    ofacPrograms: [],
    taxHavens: [],
    euHighRisk: [],
    customStandard: [],
    customMedium: [],
    customHigh: [],
    rules: DEFAULT_COUNTRY_RISK_RULES,
  });

  const [cpiDoc, setCpiDoc] = useState({
    year: cpiYear,
    source: "",
    scores: {},
  });

  const [baselDoc, setBaselDoc] = useState({
    year: baselYear,
    source: "",
    scores: {},
  });

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoadingGeo(true);
      setGeoErr("");

      try {
        // Global lists (FATF/OFAC/taxHavens) — always from countryRisk/lists
        const globalSnap = await getDoc(doc(db, "countryRisk", "lists"));
        const globalData = globalSnap.exists() ? globalSnap.data() : {};

        // Per-org custom lists — from countryRisk/{orgId} if available
        const orgData = orgId
          ? await getDoc(doc(db, "countryRisk", orgId)).then((s) =>
              s.exists() ? s.data() : {}
            )
          : {};

        const fatfGrey = Array.isArray(globalData?.fatfGrey) ? globalData.fatfGrey : [];
        const fatfBlack = Array.isArray(globalData?.fatfBlack) ? globalData.fatfBlack : [];
        const ofacPrograms = Array.isArray(globalData?.ofacPrograms) ? globalData.ofacPrograms : [];
        const taxHavens = Array.isArray(globalData?.taxHavens) ? globalData.taxHavens : [];
        const euHighRisk = Array.isArray(globalData?.euHighRisk) ? globalData.euHighRisk : [];
        const globalCustomStandard = Array.isArray(globalData?.customStandard) ? globalData.customStandard : [];
        const globalCustomMedium = Array.isArray(globalData?.customMedium) ? globalData.customMedium : [];
        const globalCustomHigh = Array.isArray(globalData?.customHigh) ? globalData.customHigh : [];
        const rules = sanitizeCountryRiskRules(globalData?.rules);

        const useGeneralCountryList = orgData?.useGeneralList === true;
        const customStandard = useGeneralCountryList
          ? globalCustomStandard
          : Array.isArray(orgData?.customStandard)
          ? orgData.customStandard
          : [];
        const customMedium = useGeneralCountryList
          ? globalCustomMedium
          : Array.isArray(orgData?.customMedium)
          ? orgData.customMedium
          : [];
        const customHigh = useGeneralCountryList
          ? globalCustomHigh
          : Array.isArray(orgData?.customHigh)
          ? orgData.customHigh
          : [];

        const cpiSnap = await getDoc(doc(db, "countryRisk", `cpi_${cpiYear}`));
        const cpiData = cpiSnap.exists() ? cpiSnap.data() : {};
        const scores =
          cpiData?.scores && typeof cpiData.scores === "object"
            ? cpiData.scores
            : {};

        const baselSnap = await getDoc(doc(db, "countryRisk", `basel_${baselYear}`));
        const baselData = baselSnap.exists() ? baselSnap.data() : {};
        const baselScores =
          baselData?.scores && typeof baselData.scores === "object"
            ? baselData.scores
            : {};

        if (!alive) return;

        setBaselDoc({
          year: Number(baselData?.year) || baselYear,
          source: String(baselData?.source || ""),
          scores: baselScores,
        });

        setLists({
          fatfGrey: (fatfGrey || []).map(toIso3).filter(Boolean),
          fatfBlack: (fatfBlack || []).map(toIso3).filter(Boolean),
          ofacPrograms: (ofacPrograms || []).map(toIso3).filter(Boolean),
          taxHavens: (taxHavens || []).map(toIso3).filter(Boolean),
          euHighRisk: (euHighRisk || []).map(toIso3).filter(Boolean),
          customStandard: (customStandard || []).map(toIso3).filter(Boolean),
          customMedium: (customMedium || []).map(toIso3).filter(Boolean),
          customHigh: (customHigh || []).map(toIso3).filter(Boolean),
          rules,
        });

        setCpiDoc({
          year: Number(cpiData?.year) || cpiYear,
          source: String(cpiData?.source || ""),
          scores,
        });
      } catch (e) {
        if (!alive) return;
        setGeoErr(e?.message || "Failed to load country risk data.");
      } finally {
        if (alive) setLoadingGeo(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [cpiYear, baselYear, orgId]);

  const fatfGreySet = useMemo(
    () => new Set(lists.fatfGrey || []),
    [lists.fatfGrey]
  );
  const fatfBlackSet = useMemo(
    () => new Set(lists.fatfBlack || []),
    [lists.fatfBlack]
  );
  const ofacSet = useMemo(
    () => new Set(lists.ofacPrograms || []),
    [lists.ofacPrograms]
  );
  const taxHavenSet = useMemo(
    () => new Set(lists.taxHavens || []),
    [lists.taxHavens]
  );
  const euHighRiskSet = useMemo(
    () => new Set(lists.euHighRisk || []),
    [lists.euHighRisk]
  );
  const customStandardSet = useMemo(
    () => new Set(lists.customStandard || []),
    [lists.customStandard]
  );
  const customMediumSet = useMemo(
    () => new Set(lists.customMedium || []),
    [lists.customMedium]
  );
  const customHighSet = useMemo(
    () => new Set(lists.customHigh || []),
    [lists.customHigh]
  );

  function buildRisk(iso3) {
    const hasIso = !!iso3;
    const cpiScoreRaw = hasIso ? readCountryScore(cpiDoc?.scores, iso3) : null;
    const cpiScore = cpiScoreRaw == null ? null : Number(cpiScoreRaw);
    const baselScoreRaw = hasIso ? readCountryScore(baselDoc?.scores, iso3) : null;
    const baselScore = baselScoreRaw == null ? null : Number(baselScoreRaw);

    const evaluated = evaluateCountryRiskForIso3({
      iso3,
      sourceSets: {
        fatfBlack: fatfBlackSet,
        ofacPrograms: ofacSet,
        fatfGrey: fatfGreySet,
        taxHavens: taxHavenSet,
        euHighRisk: euHighRiskSet,
      },
      customSets: {
        standard: customStandardSet,
        medium: customMediumSet,
        high: customHighSet,
      },
      rules: lists.rules,
      cpiScore: Number.isFinite(cpiScore) ? cpiScore : null,
      baselScore: Number.isFinite(baselScore) ? baselScore : null,
    });

    return {
      iso3,
      isGrey: hasIso && fatfGreySet.has(iso3),
      isBlack: hasIso && fatfBlackSet.has(iso3),
      isOfac: hasIso && ofacSet.has(iso3),
      isTaxHaven: hasIso && taxHavenSet.has(iso3),
      isEuHighRisk: hasIso && euHighRiskSet.has(iso3),
      defaultSourceKeys: evaluated.defaultSourceKeys,
      defaultTier: evaluated.defaultTier,
      customTier: evaluated.customTier,
      finalTier: evaluated.finalTier,
      usedCustomOverride: evaluated.usedCustomOverride,
      cpiScore: Number.isFinite(cpiScore) ? cpiScore : null,
      baselScore: Number.isFinite(baselScore) ? baselScore : null,
    };
  }

  const riskA = useMemo(
    () => buildRisk(isoIncorpOrNat),
    [
      isoIncorpOrNat,
      fatfGreySet,
      fatfBlackSet,
      ofacSet,
      taxHavenSet,
      euHighRiskSet,
      customStandardSet,
      customMediumSet,
      customHighSet,
      lists.rules,
      cpiDoc,
      baselDoc,
    ]
  );

  const riskB = useMemo(
    () => buildRisk(isoDomicile),
    [
      isoDomicile,
      fatfGreySet,
      fatfBlackSet,
      ofacSet,
      taxHavenSet,
      euHighRiskSet,
      customStandardSet,
      customMediumSet,
      customHighSet,
      lists.rules,
      cpiDoc,
      baselDoc,
    ]
  );

  const ownershipCountriesKey = ownershipCountrySignature(ownership);

  const selectedUboCountryRisks = useMemo(() => {
    if (!isEntity) return [];

    const uboScope = normalizeUboCountryRiskScope(lists.rules?.entityUboScope);
    return getSelectedIndividualUboRows(ownership, uboScope)
      .map((ubo, idx) => {
        const name = String(ubo?.name || "").trim() || `UBO ${idx + 1}`;
        const rowKey = String(ubo?._rowId || ubo?.treeNodeId || idx);
        const nationalityRisk = buildRisk(toIso3(ubo?.nationality));
        const domicileRisk = buildRisk(toIso3(ubo?.countryOfResidence || ubo?.domicile));
        const nationalityLevel = geoLevelFromCountryRisk(nationalityRisk);
        const domicileLevel = geoLevelFromCountryRisk(domicileRisk);

        return {
          key: rowKey,
          name,
          nationalityRisk,
          domicileRisk,
          geoLevel: combineGeoLevels(nationalityLevel, domicileLevel, lists.rules?.individualCombination),
        };
      });
  }, [
    isEntity,
    ownership?.qualifiedUbos,
    ownership?.uboAssessment?.qualifiedUbos,
    ownership?.shareholders,
    ownership?.directShareholders,
    ownership?.ubos,
    ownership?.controllingPersonsNatural,
    ownershipCountriesKey,
    fatfGreySet,
    fatfBlackSet,
    ofacSet,
    taxHavenSet,
    euHighRiskSet,
    customStandardSet,
    customMediumSet,
    customHighSet,
    lists.rules,
    cpiDoc,
    baselDoc,
  ]);

  const uboGeoRiskLevel = useMemo(
    () => selectedUboCountryRisks.reduce((level, ubo) => maxGeoLevel(level, ubo.geoLevel), null),
    [selectedUboCountryRisks]
  );

  const highestUboCountryRisks = useMemo(() => {
    if (!uboGeoRiskLevel) return [];
    if (uboGeoRiskLevel === "low") return [];
    return selectedUboCountryRisks.filter((ubo) => ubo.geoLevel === uboGeoRiskLevel);
  }, [selectedUboCountryRisks, uboGeoRiskLevel]);

  const showStandardUboSummary = isEntity && uboGeoRiskLevel === "low" && selectedUboCountryRisks.length > 0;

  const geoLevelA = useMemo(() => geoLevelFromCountryRisk(riskA), [riskA]);
  const geoLevelB = useMemo(
    () => (isEntity ? null : geoLevelFromCountryRisk(riskB)),
    [riskB, isEntity]
  );
  const geoRiskLevel = useMemo(
    () => (isEntity ? maxGeoLevel(geoLevelA, uboGeoRiskLevel) : combineGeoLevels(geoLevelA, geoLevelB, lists.rules?.individualCombination)),
    [geoLevelA, geoLevelB, uboGeoRiskLevel, isEntity, lists.rules]
  );

  useEffect(() => {
    if (loadingGeo || geoErr) return;

    const payload = {
      geoRiskLevel,
      cpiYear: cpiDoc?.year || cpiYear,
      cpiSource: cpiDoc?.source || "",
      baselYear: baselDoc?.year || baselYear,
      baselSource: baselDoc?.source || "",
      isEntity,
      incorpOrNatRaw: String(customer.nationalityOrIncorporationCountry || ""),
      domicileRaw: isEntity ? "" : String(customer.country || ""),
      riskA,
      riskB: isEntity ? null : riskB,
      uboCountryRiskScope: isEntity ? normalizeUboCountryRiskScope(lists.rules?.entityUboScope) : null,
      uboGeoRiskLevel: isEntity ? uboGeoRiskLevel : null,
      uboCountryRisks: isEntity ? selectedUboCountryRisks : [],
      individualCombination: lists.rules?.individualCombination || null,
    };

    if (typeof onGeoResult === "function") onGeoResult(payload);
  }, [
    loadingGeo,
    geoErr,
    geoRiskLevel,
    cpiDoc,
    cpiYear,
    baselDoc,
    baselYear,
    isEntity,
    customer.nationalityOrIncorporationCountry,
    customer.country,
    riskA,
    riskB,
    lists.rules,
    uboGeoRiskLevel,
    selectedUboCountryRisks,
    onGeoResult,
  ]);

  function focusBusinessActivityField() {
    const el = document.getElementById("kyc-business-activity-field");
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      el.focus({ preventScroll: true });
    }, 350);
  }

  function renderCountryBadges(r) {
    const iso3 = r?.iso3 || "";
    const noCountry = !iso3;
    const isEuHighRisk =
      r?.isEuHighRisk === true ||
      (Array.isArray(r?.defaultSourceKeys) && r.defaultSourceKeys.includes("euHighRisk"));

    return (
      <>
        {noCountry ? badge("No country selected", "secondary") : null}

        {!noCountry ? (
          <>
            {r.isBlack ? (
              badge("FATF: Black list", "danger")
            ) : r.isGrey ? (
              badge("FATF: Grey list", "warning")
            ) : (
              <span className="badge badgeStandard badgeKycGreen">
                FATF
                <i
                  className="bi bi-check-circle-fill"
                  style={{ marginLeft: 5 }}
                />
              </span>
            )}

            {r.isOfac ? (
              badge("OFAC: Active program", "warning")
            ) : (
              <span className="badge badgeStandard badgeKycGreen">
                OFAC
                <i
                  className="bi bi-check-circle-fill"
                  style={{ marginLeft: 5 }}
                />
              </span>
            )}

            {r.isTaxHaven ? (
              badge("EU Tax haven: Listed", "warning")
            ) : (
              <span className="badge badgeStandard badgeKycGreen">
                EU Tax haven
                <i
                  className="bi bi-check-circle-fill"
                  style={{ marginLeft: 5 }}
                />
              </span>
            )}

            {isEuHighRisk ? (
              badge("EU High-risk: Listed", "warning")
            ) : (
              <span className="badge badgeStandard badgeKycGreen">
                EU High-risk
                <i
                  className="bi bi-check-circle-fill"
                  style={{ marginLeft: 5 }}
                />
              </span>
            )}

            {cpiPill(r.cpiScore)}
            {baselPill(r.baselScore)}

            {r.customTier === "high" ? (
              customListBadge("Custom list: High", "high")
            ) : r.customTier === "medium" ? (
              customListBadge("Custom list: Medium", "medium")
            ) : r.customTier === "standard" ? (
              customListBadge("Custom list: Standard", "standard")
            ) : (
              <span
                className="badge badgeStandard"
                style={{ backgroundColor: "#9BB1A5", color: "#fff" }}
              >
                Custom list
                <i
                  className="bi bi-check-circle-fill"
                  style={{ marginLeft: 5 }}
                />
              </span>
            )}
          </>
        ) : null}
      </>
    );
  }

  function renderCountry(label, r) {
    const iso3 = r?.iso3 || "";
    const iso2 = iso3 ? ISO3_TO_ISO2[String(iso3).toUpperCase()] || "" : "";

    return (
      <div className="p-3 border rounded-3 mb-3" style={{ background: "#fff" }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="fw-semibold" style={{ color: "#4C5465" }}>
            {label}
          </div>
          <div
            className="text-muted small"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            {iso2 ? (
              <CircleFlag
                className="flagMutedImg"
                countryCode={iso2.toLowerCase()}
                height="18"
                style={{ flexShrink: 0 }}
              />
            ) : null}
            <span>{iso3 || "—"}</span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-2">
          {renderCountryBadges(r)}
        </div>
      </div>
    );
  }

  function renderUboCountryDetail(label, r) {
    const iso3 = r?.iso3 || "";
    const iso2 = iso3 ? ISO3_TO_ISO2[String(iso3).toUpperCase()] || "" : "";

    return (
      <div className="kycUboGeoCountry">
        <div className="kycUboGeoCountryHead">
          <span className="kycUboGeoCountryLabel">{label}</span>
          <span className="kycUboGeoCountryCode">
            {iso2 ? (
              <CircleFlag
                className="flagMutedImg"
                countryCode={iso2.toLowerCase()}
                height="16"
                style={{ flexShrink: 0 }}
              />
            ) : null}
            {iso3 || "—"}
          </span>
        </div>
        <div className="kycUboGeoCountryBadges">{renderCountryBadges(r)}</div>
      </div>
    );
  }

  function renderUboGeoRiskCard(ubo) {
    return (
      <div className="kycUboGeoCard" key={ubo.key}>
        <div className="kycUboGeoCardHead">
          <div className="kycUboGeoName">Highest-risk UBO: {ubo.name}</div>
        </div>
        <div className="kycUboGeoGrid">
          {renderUboCountryDetail("Nationality", ubo.nationalityRisk)}
          {renderUboCountryDetail("Domicile", ubo.domicileRisk)}
        </div>
      </div>
    );
  }

  // ---------------- INDUSTRY RISK ----------------
  const [loadingIndustry, setLoadingIndustry] = useState(false);
  const [industryErr, setIndustryErr] = useState("");

  const [industryAuto, setIndustryAuto] = useState(null);

  const [industryFinal, setIndustryFinal] = useState({
    mode: "pending", // pending | accepted | overridden
    riskLevel: null,
    reason: "",
    decidedAt: null,
  });

  const [showOverride, setShowOverride] = useState(false);
  const [showManualIndustryRiskSelect, setShowManualIndustryRiskSelect] =
    useState(false);
  const [showIndustryUnconfirmPrompt, setShowIndustryUnconfirmPrompt] =
    useState(false);
  const overrideReasonRef = useRef(null);

  useLayoutEffect(() => {
    if (!showOverride) return;
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(overrideReasonRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showOverride, industryFinal.reason]);

  // ✅ industry level for gauge (1=low,2=medium,3=high)
  const industryLevel = useMemo(() => {
    const hasFinal =
      industryFinal.mode !== "pending" && industryFinal.riskLevel != null;

    const effective = hasFinal
      ? Number(industryFinal.riskLevel)
      : industryAuto?.riskLevel != null
      ? Number(industryAuto.riskLevel)
      : null;

    if (effective === 1) return "low";
    if (effective === 2) return "medium";
    if (effective === 3) return "high";
    return null;
  }, [industryFinal, industryAuto]);

  async function runIndustryCheck(textOverride, { markDirty = true } = {}) {
    setIndustryErr("");
    setIndustryAuto(null);
    setIndustryFinal({
      mode: "pending",
      riskLevel: null,
      reason: "",
      decidedAt: null,
    });
    setShowOverride(false);
    setShowManualIndustryRiskSelect(false);
    setShowIndustryUnconfirmPrompt(false);

    const text = String(textOverride ?? businessActivity ?? "").trim();
    if (!text) {
      setIndustryErr("Missing text");
      return;
    }

    setLoadingIndustry(true);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : "";

      const json = await apiFetch(INDUSTRY_PATH, {
        method: "POST",
        token,
        body: {
          text,
          orgId,
          screeningUsage: buildScreeningUsageContext(form, "activities_categorisation"),
        },
      });

      const category = String(
        json?.category || json?.data?.category || ""
      ).trim();
      const scoreRaw = json?.riskLevel ?? json?.data?.riskLevel;
      const scoreNum = scoreRaw == null ? null : Number(scoreRaw);
      const activitySummary = String(
        json?.activitySummary || json?.data?.activitySummary || ""
      ).trim();
      const isRelevantBusinessActivity =
        json?.isRelevantBusinessActivity ??
        json?.data?.isRelevantBusinessActivity ??
        null;

      const auto = {
        categoryText: category,
        riskLevel: Number.isFinite(scoreNum) ? scoreNum : null,
        confidence: Number.isFinite(Number(json?.confidence))
          ? Number(json.confidence)
          : null,
        isRelevantBusinessActivity:
          typeof isRelevantBusinessActivity === "boolean"
            ? isRelevantBusinessActivity
            : null,
        activitySummary,
        checkedText: text,
        checkedAt: Date.now(),
        listSource: json?.listSource || null,
        listPath: json?.listPath || null,
      };

      setIndustryAuto(auto);
      const isNoCategory =
        String(auto.categoryText || "").trim().toLowerCase() === NO_CATEGORY;
      const hasIdentifiedCategory =
        !!String(auto.categoryText || "").trim() &&
        !isNoCategory &&
        auto.riskLevel != null;

      if (isNoCategory) {
        setShowIndustryDetails(true);
      } else if (hasIdentifiedCategory) {
        setShowIndustryDetails(false);
      }

      const final = {
        mode: hasIdentifiedCategory ? "accepted" : "pending",
        riskLevel: hasIdentifiedCategory ? auto.riskLevel : null,
        reason: "",
        decidedAt: hasIdentifiedCategory ? Date.now() : null,
      };

      setIndustryFinal(final);
      setShowOverride(false);

      onIndustryResult?.({ auto, final }, { markDirty });
    } catch (e) {
      setIndustryErr(userFriendlyIndustryError(e));
    } finally {
      setLoadingIndustry(false);
    }
  }

  function acceptIndustry() {
    if (industryAuto?.riskLevel == null) return;

    const final = {
      mode: "accepted",
      riskLevel: industryAuto.riskLevel,
      reason: "",
      decidedAt: Date.now(),
    };

    setIndustryFinal(final);
    setShowIndustryUnconfirmPrompt(false);
    onIndustryResult?.({ auto: industryAuto, final });
  }

  function unconfirmIndustry() {
    if (!industryAuto) return;

    const final = {
      mode: "pending",
      riskLevel: null,
      reason: "",
      decidedAt: null,
    };

    setIndustryErr("");
    setShowOverride(false);
    setShowIndustryUnconfirmPrompt(false);
    setIndustryFinal(final);
    onIndustryResult?.({ auto: industryAuto, final });
  }

  function saveOverrideIndustry() {
    if (!industryAuto) return;

    if (industryFinal.riskLevel == null) {
      setIndustryErr("Please select an override risk level.");
      return;
    }

    const lvl = Number(industryFinal.riskLevel);
    if (![1, 2, 3].includes(lvl)) {
      setIndustryErr("Please select an override risk level.");
      return;
    }

    const reason = String(industryFinal.reason || "").trim();
    if (!reason) {
      setIndustryErr("Please add a short reason for the override.");
      return;
    }

    setIndustryErr("");

    const final = {
      mode: "overridden",
      riskLevel: lvl,
      reason,
      decidedAt: Date.now(),
    };

    setIndustryFinal(final);
    setShowOverride(false);
    onIndustryResult?.({ auto: industryAuto, final });
  }

  function closeOverrideModal() {
    setIndustryErr("");
    setShowOverride(false);
  }

  function selectManualIndustryRiskLevel(riskLevelValue) {
    const lvl = Number(riskLevelValue || 0);
    if (![1, 2, 3].includes(lvl)) return;

    setIndustryErr("");
    setShowOverride(false);
    setShowManualIndustryRiskSelect(false);
    setShowIndustryUnconfirmPrompt(false);

    const auto = {
      ...(industryAuto || {}),
      categoryText: NO_CATEGORY,
      riskLevel: lvl,
      confidence: null,
      checkedText: businessActivity,
      checkedAt: Date.now(),
      manuallySelectedRiskLevel: true,
    };

    const final = {
      mode: "accepted",
      riskLevel: lvl,
      reason: "",
      decidedAt: Date.now(),
    };

    setIndustryAuto(auto);
    setIndustryFinal(final);
    onIndustryResult?.({ auto, final });
  }

  useEffect(() => {
    return () => {
      if (industryAutoRunTimerRef.current) {
        clearTimeout(industryAutoRunTimerRef.current);
        industryAutoRunTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (initialIndustryAutoloadDoneRef.current) return;
    if (!businessActivity) return;
    if (loadingIndustry) return;

    const saved = form?.internalUse?.industryRiskResult;
    const hasSavedAssessment =
      saved &&
      (saved?.auto?.riskLevel != null ||
        String(saved?.auto?.checkedText || "").trim() ||
        saved?.final?.riskLevel != null);

    if (hasSavedAssessment) {
      initialIndustryAutoloadDoneRef.current = true;
      return;
    }

    initialIndustryAutoloadDoneRef.current = true;
    runIndustryCheck(businessActivity, { markDirty: false });
  }, [businessActivity, form?.internalUse?.industryRiskResult, loadingIndustry]);

  useEffect(() => {
    const previous = prevBusinessActivityRef.current;
    if (previous === businessActivity) return;

    prevBusinessActivityRef.current = businessActivity;

    if (industryAutoRunTimerRef.current) {
      clearTimeout(industryAutoRunTimerRef.current);
      industryAutoRunTimerRef.current = null;
    }

    if (!businessActivity) return;

    industryAutoRunTimerRef.current = setTimeout(() => {
      const latestCheckedText = String(
        form?.internalUse?.industryRiskResult?.auto?.checkedText || ""
      ).trim();

      if (!businessActivity || businessActivity === latestCheckedText) return;
      runIndustryCheck(businessActivity, { markDirty: false });
    }, 900);
  }, [businessActivity, form?.internalUse?.industryRiskResult?.auto?.checkedText]);

  function renderIndustryRisk() {
    const noActivity = !businessActivity;
    const hasAuto = !!industryAuto;
    const activityDisplayText =
      String(industryAuto?.activitySummary || "").trim() ||
      shortBusinessActivityFallback(businessActivity);

    const hasFinal =
      industryFinal.mode !== "pending" && industryFinal.riskLevel != null;

    const effectiveRisk = hasFinal
      ? industryFinal.riskLevel
      : industryAuto?.riskLevel ?? null;

    const assessedLvl =
      industryAuto?.riskLevel != null ? Number(industryAuto.riskLevel) : null;

    const overrideOptions = [
      { value: 1, label: "Standard risk", tone: "low" },
      { value: 2, label: "Medium risk", tone: "medium" },
      { value: 3, label: "High risk", tone: "high" },
    ].filter((o) => o.value !== assessedLvl);
    const manualRiskLevelOptions = [
      { value: 1, label: "Standard risk", tone: "low", helper: "Standard activity" },
      { value: 2, label: "Medium risk", tone: "medium", helper: "Review advised" },
      { value: 3, label: "High risk", tone: "high", helper: "Enhanced review" },
    ];
    const hasAssessedRisk =
      industryAuto?.riskLevel != null && !industryAuto?.manuallySelectedRiskLevel;

    const showAcceptBtn =
      hasAuto &&
      hasAssessedRisk &&
      (showOverride || industryFinal.mode !== "accepted") &&
      // Undoing an existing override is override-level work too.
      !(readOnly && industryFinal.mode === "overridden");

    const showOverrideBtn =
      !readOnly &&
      hasAuto &&
      hasAssessedRisk &&
      !showOverride &&
      industryFinal.mode !== "overridden";

    const showReasonBlock =
      hasFinal && industryFinal.mode === "overridden" && !!industryFinal.reason;

    const detailKey = showReasonBlock
      ? "detail_reason"
      : "detail_none";
    const isNoCategoryIdentified =
      String(industryAuto?.categoryText || "").trim().toLowerCase() ===
      NO_CATEGORY;
    const isManualNoCategoryRisk =
      isNoCategoryIdentified && !!industryAuto?.manuallySelectedRiskLevel;
    const showManualRiskSelect =
      isNoCategoryIdentified &&
      (!isManualNoCategoryRisk || showManualIndustryRiskSelect);

    return (
      <motion.div
        className="p-3 border rounded-3"
        style={{
          background: "#fff",
          position: "relative",
        }}
        layout
        transition={LAYOUT_TRANSITION}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="fw-semibold" style={{ color: "#4C5465" }}>
            Industry risk
          </div>

          <div className="d-flex gap-2">
            {!hasAuto && !noActivity ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={runIndustryCheck}
                disabled={loadingIndustry}
                title="Call API"
              >
                {loadingIndustry ? "Assessing…" : "Assess industry risk"}
              </button>
            ) : null}

            {hasAuto && showAcceptBtn ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => {
                  setIndustryErr("");
                  setShowOverride(false);
                  acceptIndustry();
                }}
                disabled={industryAuto?.riskLevel == null}
                title="Use AI risk as final"
              >
                {industryFinal.mode === "overridden"
                  ? "Undo override"
                  : "Confirm"}
              </button>
            ) : null}

            {hasAuto && showOverrideBtn ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => {
                  setIndustryErr("");
                  setShowOverride(true);
                  setIndustryFinal((prev) => ({
                    ...prev,
                    riskLevel: null,
                  }));
                }}
                title="Override the final risk"
              >
                Override
              </button>
            ) : null}

            {hasAuto && isManualNoCategoryRisk && !showManualIndustryRiskSelect ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => {
                  setIndustryErr("");
                  setShowManualIndustryRiskSelect(true);
                }}
                title="Edit manually selected risk"
              >
                Edit
              </button>
            ) : null}
          </div>
        </div>

        {!hasAuto && noActivity ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
            style={{
              position: "absolute",
              top: "50%",
              right: 12,
              transform: "translateY(-50%)",
              zIndex: 1,
              width: 240,
              minHeight: 38,
              padding: "6px 18px",
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              fontSize: 12,
            }}
            onClick={focusBusinessActivityField}
            title="Go to Business activity"
          >
            <img
              src={doubleTapFullIcon}
              alt=""
              aria-hidden="true"
              style={{ width: 28, height: 28, flex: "0 0 auto" }}
            />
            Enter business activity details
          </button>
        ) : null}

        <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
          {noActivity
            ? badge("No business activity provided", "secondary")
            : null}

          {!noActivity && hasAuto ? (
            <>
              {industryAuto.categoryText
                ? badge(`Category: ${industryAuto.categoryText}`, "secondary")
                : null}

              {industryAuto.riskLevel != null
                ? riskLevelPill(industryAuto.riskLevel)
                : badge("Risk: n/a", "secondary")}

              {showManualRiskSelect ? (
                <ManualIndustryRiskDropdown
                  value={
                    industryAuto?.manuallySelectedRiskLevel
                      ? industryAuto?.riskLevel || ""
                      : ""
                  }
                  options={manualRiskLevelOptions}
                  onChange={selectManualIndustryRiskLevel}
                  autoOpen={showManualIndustryRiskSelect}
                  showPlaceholderWhenOpen={isManualNoCategoryRisk}
                />
              ) : null}

              {industryFinal.mode === "overridden" && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <i
                    className="bi bi-arrow-right"
                    style={{ color: "#C88294", fontSize: 18 }}
                  />
                  <i
                    className="bi bi-person-x"
                    style={{
                      color: "#C88294",
                      fontSize: 18,
                      marginLeft: 5,
                      marginRight: 5,
                    }}
                  />
                  <span style={{ color: "#C88294", fontSize: 12 }}>
                    Overridden
                  </span>
                  <i
                    className="bi bi-arrow-right"
                    style={{
                      color: "#C88294",
                      fontSize: 18,
                      marginLeft: 5,
                      marginRight: 5,
                    }}
                  />
                  <span className="ms-auto">
                    {badge(
                      ` ${
                        effectiveRisk === 1
                          ? "Standard Risk"
                          : effectiveRisk === 2
                          ? "Medium Risk"
                          : "High Risk"
                      }`,
                      effectiveRisk === 3
                        ? "danger"
                        : effectiveRisk === 2
                        ? "warning"
                        : "success"
                    )}
                  </span>
                </div>
              )}

              {industryFinal.mode === "accepted" && !showManualIndustryRiskSelect && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <i
                    className="bi bi-arrow-right"
                    style={{ color: "#9BB1A5", fontSize: 18 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowIndustryUnconfirmPrompt(true)}
                    style={{
                      alignItems: "center",
                      background: "transparent",
                      border: 0,
                      color: "#9BB1A5",
                      cursor: "pointer",
                      display: "inline-flex",
                      fontSize: 12,
                      gap: 5,
                      marginLeft: 5,
                      marginRight: 5,
                      padding: 0,
                    }}
                    title="Change confirmation"
                  >
                    <span>Confirmed</span>
                    <i
                      className="bi bi-check2-circle"
                      style={{ color: "#9BB1A5", fontSize: 18 }}
                    />
                  </button>

                  <AnimatePresence>
                    {showIndustryUnconfirmPrompt ? (
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        transition={{ duration: 0.14 }}
                        style={{
                          background: "#fff",
                          border: "1px solid #D9DDE4",
                          borderRadius: 8,
                          boxShadow: "0 10px 24px rgba(33, 37, 41, 0.12)",
                          minWidth: 168,
                          padding: 8,
                          position: "absolute",
                          right: 0,
                          top: "calc(100% + 6px)",
                          zIndex: 20,
                        }}
                      >
                        <div
                          style={{
                            color: "#4C5465",
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 7,
                          }}
                        >
                          Remove confirmation?
                        </div>
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowIndustryUnconfirmPrompt(false)}
                            style={{ fontSize: 12, padding: "2px 8px" }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={unconfirmIndustry}
                            style={{ fontSize: 12, padding: "2px 8px" }}
                          >
                            Unconfirm
                          </button>
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              )}
            </>
          ) : null}

          {!noActivity && !hasAuto && !loadingIndustry ? (
            <span
              className="text-muted d-inline-flex align-items-center gap-2"
              title="Industry risk not checked yet"
            >
              <i className="bi bi-patch-question" />
              <span className="small">Not assessed</span>
            </span>
          ) : null}

          {loadingIndustry ? (
            <span
              className="text-muted d-inline-flex align-items-center gap-2"
              title="Assessing industry risk"
            >
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              <span className="small">Assessing…</span>
            </span>
          ) : null}
        </div>

        {activityDisplayText ? (
          <div className="industryActivitySummary">
            <span className="industryActivitySummaryLabel">
              Business activity summary
            </span>
            <span className="industryActivitySummaryText">
              {activityDisplayText}
            </span>
          </div>
        ) : null}

        <AnimatePresence initial={false} mode="wait">
          {detailKey === "detail_reason" ? (
            <motion.div
              key="detail_reason"
              style={{ overflow: "hidden" }}
              {...expandCollapse}
            >
              <div className="mt-2">
                <div className="text-muted small mb-1">Override reason</div>
                <div
                  className="p-2 border rounded-3 small"
                  style={{ background: "#fafafa" }}
                >
                  {industryFinal.reason}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div key="detail_none" />
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {showOverride ? (
            <motion.div
              className="kycRiskOverrideModalOverlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              onMouseDown={closeOverrideModal}
            >
              <motion.div
                className="kycRiskOverrideModal"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.18, ease: EASE }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="kycRiskOverrideModalHeader">
                  <div className="kycRiskOverrideModalHeaderMain">
                    <div className="kycRiskOverrideModalIconWrap">
                      <Lottie
                        animationData={industryAnim}
                        loop
                        autoplay
                        className="kycRiskOverrideModalIcon"
                      />
                    </div>
                    <div className="kycRiskOverrideModalHeaderText">
                      <div className="kycRiskOverrideModalTitle">
                        Override industry risk
                      </div>
                      <div className="kycRiskOverrideModalHint">
                        Select the final risk level and indicate the rationale
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="kycRiskOverrideModalClose"
                    onClick={closeOverrideModal}
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>

                <div className="kycRiskOverrideModalBody">
                  <div className="kycRiskOverrideRiskFlow">
                    <div className="kycRiskOverrideCurrentRisk">
                      <span className="kycRiskOverrideCurrentRiskLabel">
                        Current risk
                      </span>
                      {riskLevelPill(assessedLvl)}
                    </div>

                    <i
                      className="bi bi-arrow-right kycRiskOverrideFlowArrow"
                      aria-hidden="true"
                    />

                    <div className="kycRiskOverrideTargetRisk">
                      <div className="kycRiskOverrideModalLabel">
                        Override risk to :
                      </div>
                      <ManualIndustryRiskDropdown
                        value={industryFinal.riskLevel ?? ""}
                        options={overrideOptions}
                        onChange={(value) => {
                          setIndustryErr("");
                          setIndustryFinal((p) => ({
                            ...p,
                            riskLevel: Number(value || 0) || null,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="kycRiskOverrideModalLabel">
                      Reason (required)
                    </div>
                    <textarea
                      ref={overrideReasonRef}
                      className="form-control kycRiskOverrideModalInput"
                      rows={1}
                      value={industryFinal.reason}
                      onChange={(e) => {
                        autoResizeTextarea(e.currentTarget);
                        setIndustryErr("");
                        setIndustryFinal((p) => ({
                          ...p,
                          reason: e.target.value,
                        }));
                      }}
                      onInput={(e) => autoResizeTextarea(e.currentTarget)}
                      placeholder="Reason for overriding auto risk categorisation"
                    />
                  </div>

                  {industryErr ? (
                    <div className="kycRiskOverrideModalError">
                      {industryErr}
                    </div>
                  ) : null}
                </div>

                <div className="kycRiskOverrideModalFooter">
                  <button
                    type="button"
                    className="kycRiskOverrideModalSave"
                    onClick={saveOverrideIndustry}
                  >
                    Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {industryErr && !showOverride ? (
            <motion.div
              key="industry_err"
              style={{ overflow: "hidden" }}
              {...expandCollapse}
            >
              <div className="alert alert-danger mt-2 mb-0">{industryErr}</div>
            </motion.div>
          ) : null}
        </AnimatePresence>

      </motion.div>
    );
  }

  // *************  RETURN  **************

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        <h5 className="kycSectionTitle">{sectionNo} | Risk Assessment</h5>
      </div>
      <motion.div
        className="kycSectionBody"
        layout
        transition={LAYOUT_TRANSITION}
      >
        {loadingGeo ? <div className="text-muted">Loading…</div> : null}
        {geoErr ? (
          <div className="alert alert-danger mt-2 mb-0">{geoErr}</div>
        ) : null}

        {/* ✅ Two-column layout */}
        <div className="row g-3 align-items-start mt-1">
          {/* LEFT COLUMN */}
          <div className="col-12 col-lg-6">
            {/* ✅ GEO HEADER (same layout as industry header) */}
            <div
              className="p-3 border rounded-3 kycRiskHeader"
              style={{ background: "#F6F4F2", minHeight: 118 }}
            >
              {/* LEFT: title */}
              <div className="kycRiskHeaderLeft">
                <div>
                  <div className="fw-semibold" style={{ color: "#4C5465" }}>
                    Geography risk
                  </div>
                  <div className="small text-muted" style={{ fontWeight: 100 }}>
                    {isEntity
                      ? "Based on incorporation country and UBOs"
                      : "Based on nationality and domicile"}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary kycRiskDetailsToggle"
                  onClick={(e) => {
                    setShowGeoDetails((open) => !open);
                    e.currentTarget.blur();
                  }}
                >
                  {showGeoDetails ? (
                    <>
                      <i className="bi bi-chevron-up me-1" />
                      Hide details
                    </>
                  ) : (
                    <>
                      <i className="bi bi-chevron-down me-1" />
                      Show details
                    </>
                  )}
                </button>
              </div>

              {/* RIGHT: gauge + big lottie */}
              <div className="kycRiskHeaderRight">
                <div className="kycRiskHeaderGauge">
                  <Gauge
                    title=""
                    level={geoRiskLevel}
                    rightIcon={
                      <Lottie
                        animationData={geoAnim}
                        loop
                        autoplay
                        style={{ width: 85, height: 85 }}
                      />
                    }
                  />
                </div>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {showGeoDetails ? (
                <motion.div
                  key="geo_details_block"
                  style={{ overflow: "hidden" }}
                  {...expandCollapse}
                >
                  <div className="mt-3">
                    {renderCountry(
                      isEntity ? "Incorporation country" : "Nationality",
                      riskA
                    )}
                    {!isEntity ? renderCountry("Domicile", riskB) : null}
                    {isEntity && highestUboCountryRisks.length ? (
                      <div className="mt-2">
                        {highestUboCountryRisks.map((ubo) => renderUboGeoRiskCard(ubo))}
                      </div>
                    ) : null}
                    {showStandardUboSummary ? (
                      <div className="kycUboGeoSummary mt-2">
                        <div className="kycUboGeoSummaryTitle">Relevant UBOs</div>
                        <div className="kycUboGeoSummaryBadges">
                          <span className="badge badgeStandard badgeKycGreen">
                            No elevated risk identified
                            <i
                              className="bi bi-check-circle-fill"
                              style={{ marginLeft: 5 }}
                            />
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          {/* RIGHT COLUMN */}
          <div className="col-12 col-lg-6">
            {/* ✅ INDUSTRY HEADER (keep your existing “fine” layout, but same minHeight) */}

            <div
              className="p-3 border rounded-3 kycRiskHeader"
              style={{ background: "#F6F4F2", minHeight: 118 }}
            >
              {/* LEFT: title */}
              <div className="kycRiskHeaderLeft">
                <div>
                  <div className="fw-semibold" style={{ color: "#4C5465" }}>
                    Industry risk
                  </div>
                  <div className="small text-muted" style={{ fontWeight: 100 }}>
                    Based on business activity categorization
                  </div>
                </div>
                <span className="kycRiskDetailsToggleWrap">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary kycRiskDetailsToggle"
                    onClick={(e) => {
                      setShowIndustryDetails((open) => !open);
                      e.currentTarget.blur();
                    }}
                  >
                    {showIndustryDetails ? (
                      <>
                        <i className="bi bi-chevron-up me-1" />
                        Hide details
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-1" />
                        Show details
                      </>
                    )}
                  </button>
                  {loadingIndustry ? (
                    <span
                      className="spinner-border spinner-border-sm kycRiskDetailsToggleSpinner"
                      role="status"
                      aria-label="Assessing industry risk"
                    />
                  ) : null}
                </span>
              </div>

              {/* RIGHT: gauge + lottie inside the gauge frame */}
              <div className="kycRiskHeaderRight">
                <div className="kycRiskHeaderGauge">
                  <Gauge
                    title=""
                    level={industryLevel}
                    rightIcon={
                      <Lottie
                        animationData={industryAnim}
                        loop
                        autoplay
                        style={{ width: 85, height: 85 }}
                      />
                    }
                  />
                </div>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {showIndustryDetails ? (
                <motion.div
                  key="industry_details_block"
                  style={{ overflow: "hidden" }}
                  {...expandCollapse}
                >
                  <div className="mt-3">{renderIndustryRisk()}</div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
